import React, { useState, useCallback } from 'react';
import { callSearchLegalDatabases } from '../../../utils/apiClient';
import {
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Database configurations with status indicators
const LII_DATABASES = [
  { value: 'all', label: 'All Sources', status: 'active', description: 'Search all available legal collections' },
  { value: 'zimlii', label: 'Zimbabwe Official Records', status: 'active', description: 'Zimbabwe judicial records and legislation', region: 'Zimbabwe' },
  { value: 'africanlii', label: 'African Public Records', status: 'active', description: 'African public legal repositories', region: 'Africa' },
  { value: 'saflii', label: 'Southern Africa', status: 'limited', description: 'Southern African public legal records', region: 'Southern Africa' },
  { value: 'nigerialii', label: 'Nigeria', status: 'limited', description: 'Nigerian public legal records', region: 'Nigeria' },
  { value: 'kenyalii', label: 'Kenya', status: 'limited', description: 'Kenyan public legal records', region: 'Kenya' },
];

const LegalDatabaseSearchPage = () => {
  const [params, setParams] = useState({
    query: '',
    database: 'all',
    jurisdiction: '',
    doc_type: '',
    date_from: '',
    date_to: '',
  });
  const [results, setResults] = useState(null);
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleChange = (e) => {
    setParams({ ...params, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResults(null);
    setSearchMetadata(null);

    try {
      const payload = { query: params.query, database: params.database };
      if (params.jurisdiction) payload.jurisdiction = params.jurisdiction;
      if (params.doc_type) payload.doc_type = params.doc_type;
      if (params.date_from) payload.date_from = params.date_from;
      if (params.date_to) payload.date_to = params.date_to;

      const data = await callSearchLegalDatabases(payload);
      setResults(data.items || []);
      setSearchMetadata({
        total: data.total_results || (data.items?.length || 0),
        databases: data.databases_searched || [],
        query: data.query
      });
    } catch (err) {
      console.error('Legal DB Search error:', err);
      let errorMessage = 'An error occurred while searching legal databases.';
      if (err.message?.includes('Network')) {
        errorMessage = 'Network error - please check your connection and try again.';
      } else if (err.message?.includes('timeout') || err.message?.includes('Timeout')) {
        errorMessage = 'Search timed out. Try a more specific query or different database.';
      } else if (err.message?.includes('500')) {
        errorMessage = 'Server error - the search service may be temporarily unavailable.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {

      setIsLoading(false);
    }
  };

  const formatCitation = useCallback((item) => {
    const parts = [];
    if (item.title) parts.push(item.title);
    if (item.court) parts.push(`Court: ${item.court}`);
    if (item.jurisdiction || item.region) parts.push(`Jurisdiction: ${item.jurisdiction || item.region || 'Not specified'}`);
    if (item.source) parts.push(`Source: ${item.source}`);
    const accessed = new Date().toISOString().slice(0, 10);
    parts.push(`Accessed: ${accessed}`);
    if (item.url) parts.push(item.url);
    return parts.join('. ').replace(/[<>]/g, '');
  }, []);

  const copyToClipboard = useCallback((text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'limited': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Legal Database Search</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Search across African Legal Information Institutes for cases, legislation, and legal documents
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Search Form - Left Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 sticky top-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Query Input */}
                <div>
                  <label htmlFor="query" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search Query
                  </label>
                  <input
                    type="text"
                    name="query"
                    id="query"
                    value={params.query}
                    onChange={handleChange}
                    placeholder="e.g., property rights, murder conviction"
                    required
                    className="block w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Database Select */}
                <div>
                  <label htmlFor="database" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Database
                  </label>
                  <select
                    name="database"
                    id="database"
                    value={params.database}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 rounded-md text-gray-900 dark:text-gray-100"
                  >
                    {LII_DATABASES.map(db => (
                      <option key={db.value} value={db.value}>
                        {db.label} {db.status === 'active' ? '●' : '○'}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ● Live API | ○ Limited
                  </p>
                </div>

                {/* Document Type */}
                <div>
                  <label htmlFor="doc_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document Type
                  </label>
                  <select
                    name="doc_type"
                    id="doc_type"
                    value={params.doc_type}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 rounded-md text-gray-900 dark:text-gray-100"
                  >
                    <option value="">All Types</option>
                    <option value="judgment">Case Law / Judgments</option>
                    <option value="legislation">Legislation / Acts</option>
                    <option value="gazette">Gazettes</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="date_from" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      From
                    </label>
                    <input
                      type="date"
                      name="date_from"
                      id="date_from"
                      value={params.date_from}
                      onChange={handleChange}
                      className="block w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="date_to" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      To
                    </label>
                    <input
                      type="date"
                      name="date_to"
                      id="date_to"
                      value={params.date_to}
                      onChange={handleChange}
                      className="block w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !params.query.trim()}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                      Search Databases
                    </>
                  )}
                </button>
              </form>

              {/* Database Status */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Database Status
                </h3>
                <div className="space-y-1">
                  {LII_DATABASES.filter(db => db.value !== 'all').slice(0, 4).map(db => (
                    <div key={db.value} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">{db.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(db.status)}`}>
                        {db.status === 'active' ? 'Live' : 'Limited'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results - Right Panel */}
          <div className="lg:col-span-3">
            {/* Error State */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
                  <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Searching Legal Databases</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Querying legal databases across jurisdictions...</p>
              </div>
            )}

            {/* Results */}
            {results && !isLoading && (
              <div>
                {/* Results Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {results.length} Results
                    </h2>
                    {searchMetadata?.databases && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Searched: {searchMetadata.databases.join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {results.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
                    <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Results Found</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Try adjusting your search terms or filters
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                    {results.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Title */}
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2" title={item.title}>
                              {item.title}
                            </h3>

                            {/* Metadata Badges */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.source === 'ZimLII' || item.source === 'Zimbabwe' || item.source === 'Zimbabwe Official Records'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                }`}>
                                {item.source || 'Official Records'}
                              </span>
                              {item.type && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 capitalize">
                                  {item.type}
                                </span>
                              )}
                              {item.court && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={item.court}>
                                  {item.court}
                                </span>
                              )}
                              {item.date && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {item.date}
                                </span>
                              )}
                            </div>

                            {/* Excerpt */}
                            {item.excerpt && (
                              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                                {item.excerpt}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0 flex flex-col items-end gap-2">
                            {/* Relevance Score */}
                            <div className="flex items-center gap-1">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary-500 rounded-full"
                                  style={{ width: `${(item.relevance_score || 0.8) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 w-8">
                                {((item.relevance_score || 0.8) * 100).toFixed(0)}%
                              </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1">
                              {/* Copy Citation */}
                              {item.title && (
                                <button
                                  onClick={() => copyToClipboard(formatCitation(item), item.id)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                  title="Copy citation"
                                >
                                  {copiedId === item.id ? (
                                    <CheckIcon className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                  )}
                                </button>
                              )}

                              {/* View Source */}
                              {item.url ? (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                                >
                                  View
                                  <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400 px-3 py-1.5">N/A</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {!results && !isLoading && !error && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                  <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Search Legal Databases</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                  Enter a search query to find cases, legislation, and legal documents from African Legal Information Institutes
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDatabaseSearchPage;
